import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { PRIZE_METRICS, PRIZE_METRIC_LABELS, MAX_PRIZES_PER_PROGRAM, PLATFORM_LABELS } from '@affily/shared/constants';
import { formatCents } from '@affily/shared/money';
import { api } from '../../lib/api.js';
import { useFetch } from '../../hooks/useFetch.js';
import { nestedError } from '../../lib/fieldRules.js';
import {
  prizeMeasure,
  prizeValue,
  ordinal,
  COMPETITION_STATUS_LABELS,
  toLocalInput,
  fromLocalInput,
} from '../../lib/competition.js';
import { DashboardLayout } from '../../components/DashboardLayout.jsx';
import {
  Button,
  Field,
  Input,
  Select,
  Alert,
  Badge,
  Spinner,
  SectionTitle,
  EmptyState,
} from '../../components/ui.jsx';
import { HelpLink } from '../Help.jsx';
import { ProgramSteps, NextStep } from '../../components/ProgramSteps.jsx';

const statusTone = { upcoming: 'neutral', running: 'positive', ended: 'caution' };

const newPrize = (events) => ({
  name: '',
  description: '',
  kind: 'most',
  threshold: '',
  metric: 'event_count',
  eventKey: events.find((e) => !e.manualOnly)?.key ?? events[0]?.key ?? '',
  amount: '',
});

/**
 * A program's competition: what is up for grabs, who leads, and paying the
 * winners.
 *
 * Prizes are paid through Affily like commissions — awarding one puts it on
 * the brand's bill and in the winner's earnings — and only by a person, after
 * the end, with the standings and what they're made of in front of them.
 */
export default function ProgramCompetition() {
  const { id } = useParams();
  const programs = useFetch('/programs/mine/list');
  const standings = useFetch(`/programs/${id}/competition`);
  const program = programs.data?.programs?.find((p) => p.id === id) ?? null;
  const events = program?.events ?? [];
  const currency = (program?.currency ?? 'usd').toUpperCase();
  const money = (cents) => formatCents(cents, { currency, dropZeroCents: true });

  const [form, setForm] = useState(null);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!program) return;
    const c = program.competition;
    setForm(
      c
        ? {
            startsAt: toLocalInput(c.startsAt),
            endsAt: toLocalInput(c.endsAt),
            prizes: c.prizes.map((p) => ({
              ...p,
              description: p.description ?? '',
              eventKey: p.eventKey ?? '',
              kind: p.kind ?? 'most',
              threshold: p.threshold ? String(p.threshold) : '',
              amount: String(p.amountCents / 100),
            })),
          }
        : null,
    );
  }, [program]);

  const setPrize = (i, patch) => {
    setForm((f) => ({ ...f, prizes: f.prizes.map((p, x) => (x === i ? { ...p, ...patch } : p)) }));
    setSaved(false);
  };

  async function save(competition) {
    setBusy(true);
    setError('');
    setFieldErrors({});
    try {
      await api.patch(`/programs/${id}`, { competition });
      setSaved(true);
      programs.reload();
      standings.reload();
    } catch (err) {
      setError(err.message);
      setFieldErrors(err.details ?? {});
    } finally {
      setBusy(false);
    }
  }

  const submit = () =>
    save({
      startsAt: fromLocalInput(form.startsAt),
      endsAt: fromLocalInput(form.endsAt),
      prizes: form.prizes.map((p) => ({
        ...(p.key ? { key: p.key } : {}),
        name: p.name,
        description: p.description || undefined,
        metric: p.metric,
        eventKey: p.metric === 'event_count' ? p.eventKey || null : null,
        kind: p.metric === 'event_count' ? 'most' : p.kind,
        threshold: p.kind === 'first_to' && p.metric !== 'event_count' ? Number(p.threshold || 0) : null,
        amountCents: Math.round(Number(p.amount || 0) * 100),
      })),
    });

  if (programs.loading) {
    return (
      <DashboardLayout side="brand">
        <Spinner />
      </DashboardLayout>
    );
  }

  const live = standings.data?.competition ?? program?.competition ?? null;
  // The server's rule: any creator who has ever joined, ended partnerships included.
  const locked = Boolean(program?.hasCreators);

  return (
    <DashboardLayout
      side="brand"
      title="Invites & prizes"
      description={
        program ? (
          <>
            {program.title} · <HelpLink slug="how-do-competitions-work">How competitions work</HelpLink>
          </>
        ) : undefined
      }
    >
      <ProgramSteps programId={id} current="invites" program={program} />
      <div className="max-w-3xl space-y-10">
        {/* Both are optional: a program can go live with neither. */}
        <section>
          <SectionTitle hint="Optional">Invite creators</SectionTitle>
          <p className="text-sm text-ink-muted">
            Find creators who fit and invite them straight to this program. You can invite now;
            they can accept once it is published.
          </p>
          <Button to="/dashboard/brand/creators" variant="outline" size="sm" className="mt-3">
            Find creators to invite
          </Button>
        </section>

        {Object.keys(fieldErrors).length > 0 ? (
          <Alert>
            {Object.values(fieldErrors).map((message, i) => (
              <span key={i} className="block">
                {message}
              </span>
            ))}
          </Alert>
        ) : (
          <Alert>{error}</Alert>
        )}
        {saved && <Alert tone="positive">Saved. The prizes are on your public program page.</Alert>}

        {/* ── Prizes ───────────────────────────────────────────────── */}
        <section>
          <SectionTitle hint="Published with the rest of your terms">Prizes</SectionTitle>

          {!form ? (
            <EmptyState
              title="No competition on this program"
              action={
                <Button
                  onClick={() =>
                    setForm({
                      // Inside the program's own dates: from its start (or now, if that has
                      // passed) to its end, or 30 days for a program that renews every year.
                      startsAt: toLocalInput(
                        new Date(Math.max(Date.now(), new Date(program?.startsAt ?? 0).getTime())).toISOString(),
                      ),
                      endsAt: toLocalInput(
                        program?.autoExtend === false && program?.endsAt
                          ? program.endsAt
                          : new Date(Date.now() + 30 * 86_400_000).toISOString(),
                      ),
                      prizes: [newPrize(events)],
                    })
                  }
                >
                  Set up a competition
                </Button>
              }
            >
              Pay prizes to the creators who lead when it ends — most viewings booked, most views on
              one video — on top of what the program already pays. Prizes are paid through Affily
              like commissions.
            </EmptyState>
          ) : (
            <div className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Starts" error={fieldErrors['competition.startsAt']}>
                  <Input
                    type="datetime-local"
                    value={form.startsAt}
                    onChange={(e) => setForm({ ...form, startsAt: e.target.value })}
                  />
                </Field>
                <Field
                  label="Ends"
                  required
                  error={fieldErrors['competition.endsAt']}
                  hint="Results are counted up to this moment."
                >
                  <Input
                    type="datetime-local"
                    value={form.endsAt}
                    onChange={(e) => setForm({ ...form, endsAt: e.target.value })}
                  />
                </Field>
              </div>

              {form.prizes.map((p, i) => (
                <div key={p.key ?? `new-${i}`} className="rounded-lg border border-rule bg-paper-raised p-4">
                  <div className="flex items-start justify-between gap-3">
                    <p className="text-2xs font-semibold uppercase tracking-wider text-ink-faint">
                      {ordinal(i + 1)} prize
                    </p>
                    {p.award ? (
                      <Badge tone="positive">Awarded to {p.award.creatorName}</Badge>
                    ) : locked && p.key ? null : (
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, prizes: form.prizes.filter((_, x) => x !== i) })}
                        className="text-xs text-negative hover:underline"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  <div className="mt-2 grid gap-3 sm:grid-cols-2">
                    <Field label="Name" required error={nestedError(fieldErrors, 'competition', 'prizes', i, 'name')}>
                      <Input
                        value={p.name}
                        placeholder="Most viral video"
                        maxLength={80}
                        onChange={(e) => setPrize(i, { name: e.target.value })}
                      />
                    </Field>
                    <Field
                      label={`Amount (${currency})`}
                      required
                      error={nestedError(fieldErrors, 'competition', 'prizes', i, 'amountCents')}
                    >
                      <Input
                        type="number"
                        min="1"
                        step="0.01"
                        value={p.amount}
                        disabled={Boolean(p.award)}
                        onChange={(e) => setPrize(i, { amount: e.target.value })}
                      />
                    </Field>
                    <Field label="Won by" error={nestedError(fieldErrors, 'competition', 'prizes', i, 'metric')}>
                      <Select
                        value={p.metric}
                        disabled={Boolean(p.award)}
                        onChange={(e) => setPrize(i, { metric: e.target.value })}
                      >
                        {PRIZE_METRICS.map((m) => (
                          <option key={m} value={m}>
                            {m === 'event_count' ? 'Most of one of your actions' : PRIZE_METRIC_LABELS[m]}
                          </option>
                        ))}
                      </Select>
                    </Field>
                    {p.metric !== 'event_count' && (
                      <Field label="How it is won" error={nestedError(fieldErrors, 'competition', 'prizes', i, 'kind')}>
                        <Select
                          value={p.kind}
                          disabled={Boolean(p.award)}
                          onChange={(e) => setPrize(i, { kind: e.target.value })}
                        >
                          <option value="most">The most by the end</option>
                          <option value="first_to">The first video to reach a number</option>
                        </Select>
                      </Field>
                    )}
                    {p.metric !== 'event_count' && p.kind === 'first_to' && (
                      <Field
                        label="Number to reach"
                        required
                        error={nestedError(fieldErrors, 'competition', 'prizes', i, 'threshold')}
                        hint="On one video, any platform. It can be awarded as soon as a video gets there."
                      >
                        <Input
                          type="number"
                          min="1"
                          step="1"
                          value={p.threshold}
                          placeholder="100000"
                          disabled={Boolean(p.award)}
                          onChange={(e) => setPrize(i, { threshold: e.target.value })}
                        />
                      </Field>
                    )}
                    {p.metric === 'event_count' && (
                      <Field label="Counting" error={nestedError(fieldErrors, 'competition', 'prizes', i, 'eventKey')}>
                        <Select
                          value={p.eventKey}
                          disabled={Boolean(p.award)}
                          onChange={(e) => setPrize(i, { eventKey: e.target.value })}
                        >
                          {events.map((e) => (
                            <option key={e.key} value={e.key}>
                              {e.name}
                            </option>
                          ))}
                        </Select>
                      </Field>
                    )}
                  </div>
                  {p.metric !== 'event_count' && (
                    <p className="mt-2 text-xs text-ink-muted">
                      {p.kind === 'first_to'
                        ? 'Read from TikTok, Instagram or YouTube through each creator’s own connected account. Videos close to the number are read every 15 minutes; two videos past it in the same read go to the one with more.'
                        : 'Read from TikTok, Instagram or YouTube through each creator’s own connected account — the best single video they enter counts.'}
                    </p>
                  )}
                </div>
              ))}

              <div className="flex flex-wrap items-center gap-3">
                {form.prizes.length < MAX_PRIZES_PER_PROGRAM && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setForm({ ...form, prizes: [...form.prizes, newPrize(events)] })}
                  >
                    Add a prize
                  </Button>
                )}
                <Button onClick={submit} disabled={busy}>
                  {busy ? 'Saving…' : 'Save competition'}
                </Button>
                {program?.competition && !locked && (
                  <Button variant="ghost" size="sm" onClick={() => save(null)} disabled={busy}>
                    Remove competition
                  </Button>
                )}
              </div>
              {locked && (
                <p className="text-xs text-ink-faint">
                  Creators have joined, so prizes can be raised or added and the end moved later — not
                  cut. They’re working towards these.
                </p>
              )}
            </div>
          )}
        </section>

        {/* ── Standings ────────────────────────────────────────────── */}
        {live && (
          <section>
            <SectionTitle
              hint={`${COMPETITION_STATUS_LABELS[live.status]} · ends ${new Date(live.endsAt).toLocaleString()}`}
              action={
                <Button size="sm" variant="outline" onClick={standings.reload}>
                  Refresh
                </Button>
              }
            >
              Standings
            </SectionTitle>
            {standings.loading ? (
              <Spinner />
            ) : (
              <div className="space-y-4">
                {live.prizes.map((prize) => (
                  <PrizeStandings
                    key={prize.key}
                    prize={prize}
                    status={live.status}
                    events={events}
                    money={money}
                    rows={standings.data?.standings?.find((s) => s.key === prize.key)?.standings ?? []}
                    programId={id}
                    onAwarded={() => {
                      standings.reload();
                      programs.reload();
                    }}
                  />
                ))}
              </div>
            )}
          </section>
        )}

        <NextStep programId={id} after="invites" />
      </div>
    </DashboardLayout>
  );
}

function PrizeStandings({ prize, status, events, money, rows, programId, onAwarded }) {
  // A first-to prize can be paid as soon as a video has reached it; the rest once it has ended.
  const awardable = !prize.award && (status === 'ended' || (prize.kind === 'first_to' && rows.length > 0));
  const [confirming, setConfirming] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function award(row) {
    setBusy(true);
    setError('');
    try {
      await api.post(`/programs/${programId}/competition/prizes/${prize.key}/award`, {
        partnershipId: row.partnershipId,
      });
      setConfirming(null);
      onAwarded();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <article className="rounded-lg border border-rule bg-paper-raised">
      <header className="flex flex-wrap items-baseline justify-between gap-2 border-b border-rule px-4 py-3">
        <div>
          <h3 className="font-medium">
            {prize.name} <span className="text-ink-muted">· {money(prize.amountCents)}</span>
          </h3>
          <p className="text-xs text-ink-muted">{prizeMeasure(prize, events)}</p>
        </div>
        {prize.award ? (
          <Badge tone="positive">
            Awarded to {prize.award.creatorName} · paid through Affily
          </Badge>
        ) : (
          <Badge tone={awardable ? 'caution' : statusTone[status]}>
            {awardable ? 'Ready to award' : COMPETITION_STATUS_LABELS[status]}
          </Badge>
        )}
      </header>

      <Alert className="m-4">{error}</Alert>
      {rows.length === 0 ? (
        <p className="px-4 py-3 text-sm text-ink-muted">
          {prize.metric === 'event_count'
            ? 'Nobody has scored yet.'
            : prize.kind === 'first_to' && status !== 'upcoming'
              ? 'No video has reached it yet.'
              : 'No videos entered yet. Creators connect TikTok, Instagram or YouTube and enter from their dashboard.'}
        </p>
      ) : (
        <ol className="divide-y divide-rule">
          {rows.map((row) => (
            <li key={row.partnershipId} className="px-4 py-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <span className="text-sm">
                  <span className="mr-2 font-semibold tabular-nums">{ordinal(row.rank)}</span>
                  {row.creator?.displayName ?? 'Creator'}
                  {row.partnershipStatus && row.partnershipStatus !== 'active' && (
                    <Badge className="ml-2">{row.partnershipStatus}</Badge>
                  )}
                </span>
                <span className="flex items-center gap-3">
                  <span className="text-sm font-medium tabular-nums">
                    {row.reachedAt
                      ? `Reached ${new Date(row.reachedAt).toLocaleString()} · ${prizeValue(prize, row.value, events)}`
                      : prizeValue(prize, row.value, events)}
                  </span>
                  {awardable && !prize.award && (
                    <Button size="sm" variant="outline" onClick={() => setConfirming(row)}>
                      Award
                    </Button>
                  )}
                </span>
              </div>

              {row.video && (
                <p className="mt-1 text-xs text-ink-muted">
                  <a href={row.video.url} target="_blank" rel="noreferrer" className="text-accent hover:underline">
                    {row.video.title || 'Their video'}
                  </a>{' '}
                  on {PLATFORM_LABELS[row.video.platform] ?? row.video.platform} ·{' '}
                  {prizeValue({ metric: 'video_views' }, row.video.metrics?.views)},{' '}
                  {prizeValue({ metric: 'video_shares' }, row.video.metrics?.shares)} ·{' '}
                  {row.video.final ? 'final count' : `read ${new Date(row.video.metricsAt).toLocaleString()}`}
                </p>
              )}
              {row.counted?.length > 0 && (
                <details className="mt-1 text-xs text-ink-muted">
                  <summary className="cursor-pointer">What was counted</summary>
                  <ul className="mt-1 space-y-0.5">
                    {row.counted.map((c) => (
                      <li key={c.orderId}>
                        <code>{c.orderId}</code> · {new Date(c.occurredAt).toLocaleString()}
                      </li>
                    ))}
                  </ul>
                  <p className="mt-1 text-ink-faint">
                    Your own ids — check them against your bookings before you pay.
                  </p>
                </details>
              )}

              {confirming?.partnershipId === row.partnershipId && (
                <div className="mt-3 rounded-md border border-caution/30 bg-caution-soft p-3 text-sm">
                  <p>
                    Pay <strong>{money(prize.amountCents)}</strong> to{' '}
                    <strong>{row.creator?.displayName ?? 'this creator'}</strong> for “{prize.name}”? It
                    goes on your bill under Settle &amp; pay and into their earnings, like a commission.
                    It can’t be undone.
                  </p>
                  <div className="mt-2 flex gap-2">
                    <Button size="sm" onClick={() => award(row)} disabled={busy}>
                      {busy ? 'Awarding…' : 'Award the prize'}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setConfirming(null)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ol>
      )}
    </article>
  );
}
